import { STAR_REGISTRY } from './starRegistry.js';
import type { InterpretationRule, RuleCondition } from './rules.js';

const PALACES = new Set(['life','siblings','spouse','children','wealth','health','travel','friends','career','property','wellbeing','parents']);
const STARS = new Set(STAR_REGISTRY.map((entry) => entry.id));
const MUTAGENS = new Set(['lu','quan','ke','ji']);
const DIGNITIES = new Set(['supportive','weakened']);
const RELATIONS = new Set(['trine','opposite']);
const HOROSCOPE_SCOPES = new Set(['decadal','minor','yearly','monthly','daily','hourly']);
const PALACE_CYCLE = ['life','siblings','spouse','children','wealth','health','travel','friends','career','property','wellbeing','parents'] as const;
const PALACE_INDEX = new Map(PALACE_CYCLE.map((id,index)=>[id,index] as const));

export interface RuleFeatureContractViolation { ruleId:string; featureId:string; reason:string; }

function validPalace(value:string):boolean { return PALACES.has(value); }
function validStar(value:string):boolean { return STARS.has(value as never); }
function validPalaceRelation(from:string,relation:string,to:string):boolean {
  const a=PALACE_INDEX.get(from as never); const b=PALACE_INDEX.get(to as never);
  if (a===undefined||b===undefined) return false;
  const distance=(b-a+12)%12;
  return relation==='opposite'?distance===6:relation==='trine'?(distance===4||distance===8):false;
}

export function isKnownProductionFeatureId(id:string):boolean {
  const p=id.split('.');
  if (p[0]==='star' && p.length===4 && validStar(p[1]!) && p[2]==='dignity' && DIGNITIES.has(p[3]!)) return true;
  if (p[0]==='relation' && p.length===4 && validPalace(p[1]!) && RELATIONS.has(p[2]!) && validPalace(p[3]!) && validPalaceRelation(p[1]!,p[2]!,p[3]!)) return true;
  if (p[0]==='palace_stem_flight' && p.length===5 && validPalace(p[1]!) && MUTAGENS.has(p[2]!) && validPalace(p[3]!) && validStar(p[4]!)) return true;
  if (p[0]==='horoscope') return validHoroscopeFeature(p);
  if (p[0]!=='palace' || !validPalace(p[1]!)) return false;
  return validPalaceFeature(p);
}
function validPalaceFeature(p:string[]):boolean {
  if (p.length===3 && p[2]==='major_star_count') return true;
  if (p.length===4 && p[2]==='has_star' && validStar(p[3]!)) return true;
  if (p.length===3 && p[2]==='is_body_palace') return true;
  if (p.length===4 && p[2]==='birth_year_mutagen' && MUTAGENS.has(p[3]!)) return true;
  if (p.length===5 && p[2]==='star' && validStar(p[3]!) && p[4]==='brightness') return true;
  if (p.length===6 && p[2]==='star' && validStar(p[3]!) && p[4]==='dignity' && DIGNITIES.has(p[5]!)) return true;
  if (p.length===6 && p[2]==='star' && validStar(p[3]!) && p[4]==='birth_year_mutagen' && MUTAGENS.has(p[5]!)) return true;
  if (p.length===6 && RELATIONS.has(p[2]!) && validPalace(p[3]!) && p[4]==='has_star' && validStar(p[5]!) && validPalaceRelation(p[1]!,p[2]!,p[3]!)) return true;
  return false;
}

function validHoroscopeFeature(p:string[]):boolean {
  if (!HOROSCOPE_SCOPES.has(p[1]!)) return false;
  if (p.length===3 && ['index','heavenly_stem','earthly_branch','nominal_age'].includes(p[2]!)) return true;
  if (p.length===6 && p[2]==='natal_palace' && validPalace(p[3]!) && p[4]==='cycle_palace' && validPalace(p[5]!)) return true;
  if (p.length===5 && p[2]==='natal_palace' && validPalace(p[3]!) && p[4]==='cycle_palace') return true;
  if (p.length===5 && p[2]==='mutagen' && MUTAGENS.has(p[3]!) && p[4]==='star') return false;
  if (p.length===6 && p[2]==='mutagen' && MUTAGENS.has(p[3]!) && p[4]==='star' && validStar(p[5]!)) return true;
  if (p.length===6 && p[2]==='natal_palace' && validPalace(p[3]!) && p[4]==='has_moving_star' && validStar(p[5]!)) return true;
  return false;
}
function collectFeatureIds(condition:RuleCondition, output=new Set<string>()):Set<string> {
  if ('all' in condition) condition.all.forEach((item)=>collectFeatureIds(item,output));
  else if ('any' in condition) condition.any.forEach((item)=>collectFeatureIds(item,output));
  else if ('not' in condition) collectFeatureIds(condition.not,output);
  else if ('featureExists' in condition) output.add(condition.featureExists);
  else if ('compare' in condition) output.add(condition.compare.featureId);
  else if ('compareFeatures' in condition) { output.add(condition.compareFeatures.leftFeatureId); output.add(condition.compareFeatures.rightFeatureId); }
  return output;
}

export function auditRuleFeatureContracts(rules:readonly InterpretationRule[]):readonly RuleFeatureContractViolation[] {
  return rules.flatMap((rule)=>[...collectFeatureIds(rule.conditions)]
    .filter((featureId)=>!isKnownProductionFeatureId(featureId))
    .map((featureId)=>({ruleId:rule.id,featureId,reason:'Feature ID does not match an extractor-backed production namespace.'})))
    .sort((a,b)=>a.ruleId.localeCompare(b.ruleId)||a.featureId.localeCompare(b.featureId));
}
